'use client'

import type {
  EvaluationAnswerValue,
  EvaluationQuestion
} from '../types/evaluation'

import { cn } from '@/shared/lib/utils'
import { Textarea } from '@/shared/ui/textarea'
import { inputClassName } from '@/shared/ui/input-styles'

interface QuestionFieldProps {
  question: EvaluationQuestion
  value: EvaluationAnswerValue | undefined
  readOnly?: boolean
  onChange: (value: EvaluationAnswerValue) => void
}

/**
 * A superfície é o Card CLARO do portal, então o texto é escuro. Escrever
 * `text-white` aqui produz texto invisível — é o mesmo tropeço de usar
 * `bg-background text-foreground`, que neste tema são o mesmo hex.
 */
export function QuestionField({
  question,
  value,
  readOnly,
  onChange
}: QuestionFieldProps) {
  if (question.kind === 'text')
    return (
      <Textarea
        rows={4}
        disabled={readOnly}
        value={typeof value === 'string' ? value : ''}
        onChange={event => onChange(event.target.value)}
        placeholder="Escreva sua resposta"
        className={cn(inputClassName, 'h-auto min-h-28 py-3')}
      />
    )

  if (question.kind === 'multiple_choice')
    return (
      <ChoiceField
        question={question}
        value={value}
        readOnly={readOnly}
        onChange={onChange}
      />
    )

  if (question.kind === 'ranking')
    return (
      <RankingField
        question={question}
        value={value}
        readOnly={readOnly}
        onChange={onChange}
      />
    )

  return null
}

/** Mesma gramática de seleção do desafio de jornada (level-play). */
const optionClass = (isSelected: boolean, readOnly?: boolean) =>
  cn(
    'flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left font-medium transition-all duration-100',
    isSelected
      ? 'bg-primary text-white ring-2 ring-primary'
      : 'bg-gray-100 text-gray-800 hover:bg-gray-200',
    readOnly ? 'cursor-default' : 'cursor-pointer'
  )

function ChoiceField({
  question,
  value,
  readOnly,
  onChange
}: QuestionFieldProps) {
  const multiple = !!question.accepts_multiple
  // A resposta guarda o UID da opção, nunca o texto: renomear a alternativa no
  // Admin não pode invalidar nem reescrever o que o aluno já marcou.
  const selected = Array.isArray(value) ? value : []

  const toggle = (uid: string) => {
    if (readOnly) return
    if (!multiple) return onChange([uid])

    onChange(
      selected.includes(uid)
        ? selected.filter(item => item !== uid)
        : [...selected, uid]
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {multiple ? (
        <p className="text-xs font-medium text-[#7F7FAD]">
          Você pode marcar mais de uma.
        </p>
      ) : null}

      {(question.options ?? []).map(option => {
        const isSelected = selected.includes(option.uid)

        return (
          <button
            key={option.uid}
            type="button"
            disabled={readOnly}
            onClick={() => toggle(option.uid)}
            aria-pressed={isSelected}
            className={optionClass(isSelected, readOnly)}
          >
            <span
              aria-hidden
              className={cn(
                'flex size-5 shrink-0 items-center justify-center border-2',
                multiple ? 'rounded-md' : 'rounded-full',
                isSelected ? 'border-white bg-white' : 'border-gray-400'
              )}
            >
              {isSelected ? (
                <span
                  className={cn(
                    'size-2.5 bg-primary',
                    multiple ? 'rounded-[3px]' : 'rounded-full'
                  )}
                />
              ) : null}
            </span>
            <span style={{ overflowWrap: 'anywhere' }}>{option.name}</span>
          </button>
        )
      })}
    </div>
  )
}

function RankingField({
  question,
  value,
  readOnly,
  onChange
}: QuestionFieldProps) {
  const min = question.scale_min ?? 1
  const max = question.scale_max ?? 5
  const scale = Array.from({ length: max - min + 1 }, (_, index) => min + index)
  const scores =
    value && typeof value === 'object' && !Array.isArray(value) ? value : {}

  const setScore = (uid: string, score: number) => {
    if (readOnly) return
    onChange({ ...scores, [uid]: score })
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs font-medium text-[#7F7FAD]">
        {min} = discordo totalmente · {max} = concordo totalmente
      </p>

      {(question.options ?? []).map(option => (
        <div
          key={option.uid}
          className="flex flex-col gap-2 rounded-xl bg-gray-100 px-4 py-3"
        >
          <p
            className="font-medium text-primary-dark"
            style={{ overflowWrap: 'anywhere' }}
          >
            {option.name}
          </p>
          <div className="flex flex-wrap gap-2">
            {scale.map(point => {
              const isSelected = scores[option.uid] === point

              return (
                <button
                  key={point}
                  type="button"
                  disabled={readOnly}
                  onClick={() => setScore(option.uid, point)}
                  aria-label={`${option.name}: nota ${point}`}
                  aria-pressed={isSelected}
                  className={cn(
                    'size-10 rounded-lg font-bold transition-all duration-100',
                    isSelected
                      ? 'bg-primary text-white ring-2 ring-primary'
                      : 'bg-white text-gray-700 hover:bg-gray-200',
                    readOnly ? 'cursor-default' : 'cursor-pointer'
                  )}
                >
                  {point}
                </button>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}
